// src/components/HeroSection.jsx
"use client";
import Image from "next/image";
// import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useState, useEffect } from "react";

// Function to get ordinal suffix (st, nd, rd, th)
const getOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return "TH";
  switch (day % 10) {
    case 1: return "ST";
    case 2: return "ND";
    case 3: return "RD";
    default: return "TH";
  }
};

// Function to format month abbreviation
const getMonthAbbr = (month) => {
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return months[month];
};

// Function to get the date string based on current time
const getDateString = () => {
  const now = new Date();
  const currentHour = now.getHours();
  
  // If it's before 7 PM (19:00), show today's date, else show tomorrow's date
  let targetDate = new Date(now);
  if (currentHour >= 19) {
    targetDate.setDate(targetDate.getDate() + 1);
  }
  
  const day = targetDate.getDate();
  const month = targetDate.getMonth();
  
  return `STARTS ${day}${getOrdinalSuffix(day)} ${getMonthAbbr(month)}`;
};

export default function HeroSection() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [dateText, setDateText] = useState("");
  const [isIndianUser, setIsIndianUser] = useState(false);

  const videoEmbed = "https://player.vimeo.com/video/815729689?h=508164155c&autoplay=1";
  const videoThumbnail = "/images/thumbgs.webp";

  // Detect user timezone for CTA link (Indian vs international)
  useEffect(() => {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const indianTimezones = ["Asia/Kolkata", "Asia/Calcutta"];
      setIsIndianUser(indianTimezones.includes(timezone));
    } catch (error) {
      console.error("Error detecting timezone:", error);
      setIsIndianUser(false);
    }
  }, []);

  // Update date text and set up interval to check for 7 PM
  useEffect(() => {
    const updateDate = () => {
      setDateText(getDateString());
    };

    // Set initial date
    updateDate();

    // Update every minute to catch the 7 PM transition
    const interval = setInterval(updateDate, 60000);

    // Calculate milliseconds until next 7 PM
    const now = new Date();
    const next7PM = new Date(now);
    next7PM.setHours(19, 0, 0, 0);
    
    // If it's already past 7 PM today, set to 7 PM tomorrow
    if (now.getHours() >= 19) {
      next7PM.setDate(next7PM.getDate() + 1);
    }
    
    const msUntil7PM = next7PM.getTime() - now.getTime();
    
    // Set a timeout to update exactly at 7 PM
    const timeout = setTimeout(() => {
      updateDate();
    }, msUntil7PM);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);
  // Function to scroll to mentorship section
  // const scrollToMentorship = () => {
  //   const mentorshipSection = document.getElementById("mentorship");
  //   if (mentorshipSection) {
  //     mentorshipSection.scrollIntoView({ behavior: "smooth" });
  //   }
  // };

  return (
    <div className="w-full bg-white py-6 md:pt-18 mt-20">
      <div className="w-[90%] md:w-[80%] mx-auto relative rounded-3xl overflow-hidden bg-[#012726]">
        <div className="relative">
          {/* Background grid image only */}
          <div className="absolute inset-0 z-0">
            <Image
              className="object-cover w-full h-full"
              src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/GS-Grid.png"
              alt="grid"
              width={1200}
              height={800}
              priority
            />
          </div>
             <div className="absolute inset-0 z-0">
            <Image
              className="object-cover w-full h-full opacity-15"
              src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/herotext.webp"
              alt="grid"
              width={1200}
              height={800}
              priority
            />
          </div>
          
          <div className="flex flex-col md:flex-row justify-between p-6 md:pl-16 relative z-10">
            {/* Left Content */}
            <div className="w-full md:w-1/2 mb-10 md:mb-0 flex items-start justify-center flex-col">
              <h1 className="text-3xl md:text-[48px] font-medium text-[#FFFAF1] leading-tight mt-6 md:mt-0 mb-4">
               Master ChatGPT & 15+ AI Tools in Just 3 Hours
              </h1>
              <p className="text-white text-sm sm:text-base font-normal opacity-90 mb-6 max-w-xl">
               Join 10M+ professionals who've transformed their productivity, automated tedious work, and future-proofed their careers-all in one live workshop.
              </p>
              
              {/* Time and Date Badges */}
              <div className="flex flex-col md:flex-row gap-2 md:gap-3 mb-8 w-full md:w-auto">
                {/* First Row - Hours and Date Badges (side by side on mobile, centered) */}
                <div className="flex flex-row md:flex-row gap-2 md:gap-3 w-full md:w-auto justify-start">
                  {/* Hours Badge */}
                  <div className="flex items-center gap-1 md:gap-2 bg-white rounded-lg px-3 md:px-4 py-2 border border-[#012726] flex-shrink-0">
                    <Image src="/images/table.svg" alt="Clock" width={20} height={20} className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                    <span className="text-[#012726] text-xs md:text-sm font-medium uppercase whitespace-nowrap">
                      3 HOURS
                    </span>
                  </div>

                  {/* Date Badge */}
                  <div className="flex items-center gap-1 md:gap-2 bg-white rounded-lg px-3 md:px-4 py-2 border border-[#012726] flex-shrink-0">
                    <Image src="/images/ccal.svg" alt="Calendar" width={20} height={20} className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                    <span className="text-[#012726] text-xs md:text-sm font-medium uppercase whitespace-nowrap">
                      {dateText || "STARTS 11TH JAN"}
                    </span>
                  </div>
                </div>

                {/* Second Row - Time Badge (full width on mobile, centered) */}
                <div className="flex items-center justify-center md:justify-start gap-1 md:gap-2 bg-white rounded-lg px-3 md:px-4 py-2 border border-[#012726] w-[84%] md:w-auto flex-shrink-0">
                  {/* Clock SVG Icon */}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0"
                  >
                    <circle cx="12" cy="12" r="10" stroke="#012726" strokeWidth="2" />
                    <path d="M12 6V12L16 14" stroke="#012726" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  <span className="text-[#012726] text-xs md:text-sm font-medium uppercase whitespace-nowrap">
                    7PM to 10PM
                  </span>
                </div>
              </div>

              <a
                href={
                  isIndianUser
                    ? "https://applications.growthschool.io/3472/18536"
                    : "https://applications.growthschool.io/4462/18604"
                }
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#33c375] shadow-[inset_0_0_0_2px_rgba(0,39,38,0.3)] cursor-pointer hover:bg-[#6AD384] text-[#012726] font-medium px-6 py-3 rounded-xl stroke-2 flex items-center transition-all"
              >
                Claim your spot
                {/* <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="10" 
                  height="13" 
                  viewBox="0 0 12 13" 
                  fill="none"
                  className="ml-2"
                >
                  <path
                    d="M1 7.11108L6 12L11 7.11108"
                    stroke="#002726"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M1 1L6 5.88889L11 1"
                    stroke="#002726"
                    strokeOpacity="0.5"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg> */}
              </a>
            </div>

            {/* Right Content - Video Thumbnail */}
            <div className="md:flex items-end justify-end w-full md:w-1/2 relative">
              <div
                className="relative w-full md:w-[400px] aspect-square rounded-xl overflow-hidden cursor-pointer"
                onClick={() => setIsVideoOpen(true)}
              >
                <Image
                  src={videoThumbnail}
                  alt="Video thumbnail"
                  fill
                  className="object-cover"
                  unoptimized
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                  <Image
                    src="/images/ctplay.svg"
                    alt="play button"
                    width={64}
                    height={64}
                    className="w-16 h-16 md:w-20 md:h-20"
                    unoptimized
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Video Popup Modal */}
      {isVideoOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="relative w-[90%] md:w-[70%] lg:w-[60%] aspect-video rounded-xl overflow-hidden">
            <iframe
              src={videoEmbed}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
              allowFullScreen
              title="Hero Video"
            ></iframe>

            {/* Close Button */}
            <button
              onClick={() => setIsVideoOpen(false)}
              className="absolute top-3 right-3 text-white text-3xl font-bold hover:text-gray-300 z-10"
              aria-label="Close video"
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
}